/**
 * Checklists — the job form a tech works at the door (PRD v2 §2, feature matrix line 79:
 * "basic checklists incl. required-before-close"). Jobber gates this behind Connect.
 *
 * A leaf module like schemas/template.ts: it takes the idempotency envelope from the spine
 * and nothing imports it back, so it cannot close a cycle.
 */
import { z } from 'zod';
import { idempotency } from './spine';

/** One line of the form. `required` is the whole feature: it blocks finishing the visit. */
export const checklistItemDraft = z.object({
  label: z.string().trim().min(1).max(200),
  required: z.boolean().optional(),
});
export type ChecklistItemDraft = z.infer<typeof checklistItemDraft>;

export const checklistTemplateCreateInput = idempotency.extend({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  items: z.array(checklistItemDraft).min(1).max(100),
});
export type ChecklistTemplateCreateInput = z.infer<typeof checklistTemplateCreateInput>;

/**
 * Editing replaces the item list wholesale — a form is a small thing edited as a whole,
 * and per-item patching would buy nothing but drift. Visits already created keep their
 * own copies, so this only shapes future ones.
 */
export const checklistTemplateUpdateInput = idempotency.extend({
  checklistTemplateId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  items: z.array(checklistItemDraft).min(1).max(100),
});
export type ChecklistTemplateUpdateInput = z.infer<typeof checklistTemplateUpdateInput>;

export const checklistTemplateIdInput = idempotency.extend({
  checklistTemplateId: z.string().uuid(),
});
export type ChecklistTemplateIdInput = z.infer<typeof checklistTemplateIdInput>;

/** Attach a form to a job — every live visit gets a copy, and so do later ones. */
export const checklistAttachInput = idempotency.extend({
  jobId: z.string().uuid(),
  checklistTemplateId: z.string().uuid(),
});
export type ChecklistAttachInput = z.infer<typeof checklistAttachInput>;

export const checklistDetachInput = idempotency.extend({
  jobChecklistId: z.string().uuid(),
});
export type ChecklistDetachInput = z.infer<typeof checklistDetachInput>;

/**
 * The tech's tick. `done` and `note` are both optional so the phone can send either one
 * alone — typing a reading shouldn't force the box, and ticking shouldn't wipe the note.
 *
 * `visitId` is redundant with the item — `visit_checklist_items.visit_id` already says which
 * stop this belongs to — and it is here anyway, because a QUEUED write has to be readable
 * without asking the server anything.
 *
 * A tick fired with no signal sits in IndexedDB until a connection comes back, possibly
 * across an app restart with no screen mounted. Two things then need to know which visit it
 * was for: the count that decides whether Finish is pressable, and the replay, which must
 * dispatch a visit's completion after that visit's own ticks. Resolving `itemId → visitId`
 * means a round trip, and being unable to make round trips is the entire situation.
 */
export const checklistItemSetInput = idempotency.extend({
  itemId: z.string().uuid(),
  visitId: z.string().uuid(),
  done: z.boolean().optional(),
  note: z.string().trim().max(1000).optional().or(z.literal('')),
});
export type ChecklistItemSetInput = z.infer<typeof checklistItemSetInput>;
